/**
 * Platform password policy — same rules as reset-password UI.
 * Enforce on plaintext before HMAC; the API only sees the hash.
 */

export const PASSWORD_POLICY_RULE_IDS = [
  'length',
  'number',
  'lowercase',
  'uppercase',
  'special',
] as const;

export type PasswordPolicyRuleId = (typeof PASSWORD_POLICY_RULE_IDS)[number];

/** i18n keys under `password.errors.*` for each rule. */
export const PASSWORD_POLICY_ERROR_KEYS: Record<PasswordPolicyRuleId, string> = {
  length: 'password.errors.length',
  number: 'password.errors.number',
  lowercase: 'password.errors.lowercase',
  uppercase: 'password.errors.uppercase',
  special: 'password.errors.special',
};

/**
 * Per-rule pass/fail for a candidate password.
 * `true` means the rule is satisfied.
 */
export function evaluatePasswordPolicy(
  password: string
): Record<PasswordPolicyRuleId, boolean> {
  return {
    length: password.length >= 8,
    number: /\d/.test(password),
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    special: /[\W]/.test(password),
  };
}

/** Whether the password satisfies every platform policy rule. */
export function meetsPasswordPolicy(password: string): boolean {
  const checks = evaluatePasswordPolicy(password);
  return PASSWORD_POLICY_RULE_IDS.every((id) => checks[id]);
}
